import { Autocomplete, CircularProgress, InputAdornment, TextField, useTheme } from '@mui/material'
import { useState } from 'react'

const US_STATES = [
  { label: 'Alabama', abbr: 'AL' },
  { label: 'Alaska', abbr: 'AK' },
  { label: 'Arizona', abbr: 'AZ' },
  { label: 'Arkansas', abbr: 'AR' },
  { label: 'California', abbr: 'CA' },
  { label: 'Colorado', abbr: 'CO' },
  { label: 'Connecticut', abbr: 'CT' },
  { label: 'Delaware', abbr: 'DE' },
  { label: 'Florida', abbr: 'FL' },
  { label: 'Georgia', abbr: 'GA' },
  { label: 'Hawaii', abbr: 'HI' },
  { label: 'Idaho', abbr: 'ID' },
  { label: 'Illinois', abbr: 'IL' },
  { label: 'Indiana', abbr: 'IN' },
  { label: 'Iowa', abbr: 'IA' },
  { label: 'Kansas', abbr: 'KS' },
  { label: 'Kentucky', abbr: 'KY' },
  { label: 'Louisiana', abbr: 'LA' },
  { label: 'Maine', abbr: 'ME' },
  { label: 'Maryland', abbr: 'MD' },
  { label: 'Massachusetts', abbr: 'MA' },
  { label: 'Michigan', abbr: 'MI' },
  { label: 'Minnesota', abbr: 'MN' },
  { label: 'Mississippi', abbr: 'MS' },
  { label: 'Missouri', abbr: 'MO' },
  { label: 'Montana', abbr: 'MT' },
  { label: 'Nebraska', abbr: 'NE' },
  { label: 'Nevada', abbr: 'NV' },
  { label: 'New Hampshire', abbr: 'NH' },
  { label: 'New Jersey', abbr: 'NJ' },
  { label: 'New Mexico', abbr: 'NM' },
  { label: 'New York', abbr: 'NY' },
  { label: 'North Carolina', abbr: 'NC' },
  { label: 'North Dakota', abbr: 'ND' },
  { label: 'Ohio', abbr: 'OH' },
  { label: 'Oklahoma', abbr: 'OK' },
  { label: 'Oregon', abbr: 'OR' },
  { label: 'Pennsylvania', abbr: 'PA' },
  { label: 'Rhode Island', abbr: 'RI' },
  { label: 'South Carolina', abbr: 'SC' },
  { label: 'South Dakota', abbr: 'SD' },
  { label: 'Tennessee', abbr: 'TN' },
  { label: 'Texas', abbr: 'TX' },
  { label: 'Utah', abbr: 'UT' },
  { label: 'Vermont', abbr: 'VT' },
  { label: 'Virginia', abbr: 'VA' },
  { label: 'Washington', abbr: 'WA' },
  { label: 'West Virginia', abbr: 'WV' },
  { label: 'Wisconsin', abbr: 'WI' },
  { label: 'Wyoming', abbr: 'WY' },
]

interface StateOption {
  label: string
  abbr: string
}

const resolveOption = (value: string | null | undefined): StateOption | null => {
  if (!value) return null
  const upper = value.trim().toUpperCase()
  return (
    US_STATES.find(s => s.abbr === upper) ??
    US_STATES.find(s => s.label.toLowerCase() === value.trim().toLowerCase()) ??
    null
  )
}

const StateAutocomplete = ({
  value,
  onChange,
  name,
  isDirty = false,
  savingFields = [],
}: {
  value?: string | null
  onChange: (fieldName: string, newValue: string) => void
  name: string
  isDirty?: boolean
  savingFields?: string[]
}) => {
  const theme = useTheme()
  const [inputValue, setInputValue] = useState('')

  const selectedOption = resolveOption(value)

  return (
    <Autocomplete
      options={US_STATES}
      getOptionLabel={option => `${option.label} (${option.abbr})`}
      value={selectedOption}
      onChange={(_, newOption) => onChange(name, newOption?.abbr ?? '')}
      inputValue={inputValue}
      onInputChange={(_, val) => setInputValue(val)}
      isOptionEqualToValue={(option, val) => option.abbr === val.abbr}
      filterOptions={(options, { inputValue: query }) => {
        const q = query.trim().toLowerCase()
        if (!q) return options
        return options.filter(
          s => s.label.toLowerCase().includes(q) || s.abbr.toLowerCase().includes(q)
        )
      }}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {savingFields.includes(name) && (
                  <InputAdornment position="end">
                    <CircularProgress size={16} thickness={5} sx={{ mr: 1 }} />
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      fullWidth
      sx={{
        '& .MuiInputBase-root': {
          bgcolor: isDirty ? theme.palette.text.dirty : theme.palette.background.input,
        },
      }}
    />
  )
}

export default StateAutocomplete
